import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  documentId,
  onSnapshot,
  query,
  where,
  doc as docRef,
  getDoc,
  getDocs,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import {
  calcularPerfilCobranzaV2,
  getFechaInicioReal,
} from "../../helpers/cobranzaInteligenteV2";

import { db } from "../../Firebase/firebaseconfig";
import * as agendaCobranza from "../../helpers/agendaCobranza";
import { toJsDate } from "../../helpers/fechas";
import notify from "../common/notify";
import {
  listarPromesasGlobal,
  crearGestionCobranza,
} from "../../services/cobranzaOperativaService";

import "./AgendaDiaria.css";

// Firestore permite hasta 30 valores en `in` y `__name__ in` desde v9.
// Mantenemos 10 para compatibilidad con APIs antiguas y para limitar el costo por lectura.
const BATCH_IN_SIZE = 10;
const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const AgendaDiaria = () => {
  const navigate = useNavigate();

  const clientesCache = useRef(new Map());
  const fraccionamientosCache = useRef(new Map());
  const gestionesPorDeuda = useRef(new Map());

  // Batch prefetch: una sola lectura por chunk en vez de N lecturas por deuda.
  const prefetchGestiones = useCallback(async (deudaIds) => {
    const pendientes = [...new Set(deudaIds.filter(Boolean))];
    gestionesPorDeuda.current.clear();
    if (!pendientes.length) return;
    await Promise.all(
      chunk(pendientes, BATCH_IN_SIZE).map(async (group) => {
        try {
          const snap = await getDocs(
            query(collection(db, "gestionesCobranza"), where("deudaId", "in", group))
          );
          snap.docs.forEach((g) => {
            const data = g.data() || {};
            const id = data.deudaId;
            if (!id) return;
            const list = gestionesPorDeuda.current.get(id) || [];
            list.push({ id: g.id, ...data });
            gestionesPorDeuda.current.set(id, list);
          });
        } catch (err) {
          console.warn("[AgendaDiaria] batch gestiones falló para chunk:", err);
        }
      })
    );
  }, []);

  const prefetchClientes = useCallback(async (clienteIds) => {
    const pendientes = [...new Set(clienteIds.filter((id) => id && !clientesCache.current.has(id)))];
    if (!pendientes.length) return;
    await Promise.all(
      chunk(pendientes, BATCH_IN_SIZE).map(async (group) => {
        try {
          const snap = await getDocs(
            query(collection(db, "clientes"), where(documentId(), "in", group))
          );
          snap.docs.forEach((d) => clientesCache.current.set(d.id, d.data() || {}));
          // marcar como buscados los que no aparecieron, evitando reintentos
          group.forEach((id) => {
            if (!clientesCache.current.has(id)) clientesCache.current.set(id, {});
          });
        } catch (err) {
          console.warn("[AgendaDiaria] batch clientes falló para chunk:", err);
        }
      })
    );
  }, []);

  const prefetchFraccionamientos = useCallback(async (fraccIds) => {
    const pendientes = [...new Set(fraccIds.filter((id) => id && !fraccionamientosCache.current.has(id)))];
    if (!pendientes.length) return;
    await Promise.all(
      chunk(pendientes, BATCH_IN_SIZE).map(async (group) => {
        try {
          const snap = await getDocs(
            query(collection(db, "fraccionamientos"), where(documentId(), "in", group))
          );
          snap.docs.forEach((d) => fraccionamientosCache.current.set(d.id, d.data()?.nombre || "—"));
          group.forEach((id) => {
            if (!fraccionamientosCache.current.has(id)) fraccionamientosCache.current.set(id, "—");
          });
        } catch (err) {
          console.warn("[AgendaDiaria] batch fraccionamientos falló para chunk:", err);
        }
      })
    );
  }, []);

  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [soloSinGestionReciente, setSoloSinGestionReciente] = useState(false);
  const [filtroFraccionamiento, setFiltroFraccionamiento] = useState("todos");
  const [filtroCobrador, setFiltroCobrador] = useState("todos");
  const [filtroRiesgo, setFiltroRiesgo] = useState("todos");
  const [vistaCompacta, setVistaCompacta] = useState(true);
  const [agruparPorFracc, setAgruparPorFracc] = useState(false);
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [promesasPendientes, setPromesasPendientes] = useState([]);
  const [atendidosHoy, setAtendidosHoy] = useState(() => new Set());

  // Modal de gestión rápida
  const [modalGestion, setModalGestion] = useState(null); // {item, tipo, resultado, nota}

  // Carga inicial de promesas
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const promesas = await listarPromesasGlobal({ estado: "pendiente" });
        if (alive) setPromesasPendientes(promesas);
      } catch (err) {
        console.error("AD: error promesas", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const getCliente = useCallback(async (clienteId) => {
    if (!clienteId) return {};

    if (clientesCache.current.has(clienteId)) {
      return clientesCache.current.get(clienteId);
    }

    const snap = await getDoc(docRef(db, "clientes", clienteId));
    const data = snap.exists() ? snap.data() : {};

    clientesCache.current.set(clienteId, data);
    return data;
  }, []);

  const getFraccionamientoNombre = useCallback(async (dataDeuda) => {
    const fraccId = agendaCobranza.getFraccionamientoIdFromDeuda(dataDeuda);

    if (!fraccId) {
      return agendaCobranza.getFraccionamientoNombreFallback(dataDeuda);
    }

    if (fraccionamientosCache.current.has(fraccId)) {
      return fraccionamientosCache.current.get(fraccId);
    }

    const snap = await getDoc(docRef(db, "fraccionamientos", fraccId));
    const nombre = snap.exists()
      ? snap.data()?.nombre || "—"
      : agendaCobranza.getFraccionamientoNombreFallback(dataDeuda);

    fraccionamientosCache.current.set(fraccId, nombre);
    return nombre;
  }, []);

  const enriquecer = useCallback(
    async (docSnap) => {
      const data = docSnap.data();

      const perfilCobranza = calcularPerfilCobranzaV2(data);

      if (!perfilCobranza?.esCobrable) {
        return null;
      }

      const cliente = await getCliente(data?.clienteId);
      const gestiones = (gestionesPorDeuda.current.get(docSnap.id) || [])
        .slice()
        .sort((a, b) => {
          const aDate = a.creadoEn?.toDate?.()?.getTime?.() || 0;
          const bDate = b.creadoEn?.toDate?.()?.getTime?.() || 0;
          return bDate - aDate;
        });
      const ultimaGestion = gestiones[0] || null;
      const ultimaGestionFecha = toJsDate(ultimaGestion?.creadoEn);
      const diasSinGestion = ultimaGestionFecha
        ? Math.floor(
            (agendaCobranza.startOfDay(new Date()) -
              agendaCobranza.startOfDay(ultimaGestionFecha)) /
              86400000
          )
        : 9999;

      let responsableNombre = "";
      if (data?.fueraPaisRegularmente && data?.responsableId) {
        const responsable = await getCliente(data.responsableId);
        responsableNombre = agendaCobranza.getNombreCompleto(responsable);
        if (responsableNombre === "Sin nombre") responsableNombre = "";
      }

      const nextDueDate = agendaCobranza.getDueTimestamp(data);
      const dueDate = nextDueDate?.toDate?.() || null;
      const hoy = agendaCobranza.startOfDay(new Date());

      const venceHoy =
        !!dueDate &&
        agendaCobranza.startOfDay(dueDate).getTime() === hoy.getTime();

      const fraccionamiento = await getFraccionamientoNombre(data);

      const saldoRestante = Number(perfilCobranza.saldoRestante || 0);
      const saldoAtrasado = Number(perfilCobranza.saldoAtrasado || 0);
      const mensualidad = Number(perfilCobranza.mensualidad || 0);
      const mesesAtraso = Number(perfilCobranza.mesesAtraso || 0);
      const mesesTranscurridos = Number(perfilCobranza.mesesTranscurridos || 0);
      const fechaInicioReal = getFechaInicioReal(data);

      const riesgo =
        perfilCobranza.prioridad === "critica"
          ? "alto"
          : perfilCobranza.prioridad === "alta"
          ? "medio"
          : "bajo";

      // Último cobrador (del historial de pagos positivos)
      const pagosArr = Array.isArray(data?.pagos) ? data.pagos : [];
      const pagosPositivos = pagosArr.filter((p) => Number(p?.cantidad || 0) > 0);
      const ultimoPago = pagosPositivos[pagosPositivos.length - 1] || null;
      const ultimoCobrador =
        ultimoPago?.cobradorNombre ||
        ultimoPago?.cobradorEmail ||
        "Sin cobrador";

      return {
        id: docSnap.id,
        clienteId: data?.clienteId || "",
        cliente: agendaCobranza.getNombreCompleto(cliente),
        responsableNombre,
        telefono: cliente?.telefono || "—",
        direccion: cliente?.direccion || "—",
        referencia: cliente?.referencia || cliente?.folio || data?.referencia || "—",
        fraccionamiento: fraccionamiento || "—",
        ultimoCobrador,
        ultimaGestion,
        diasSinGestion,

        mensualidad,
        saldoRestante,
        saldoAtrasado,
        mesesAtraso,
        mesesTranscurridos,
        fechaInicioReal,

        nextDueDate,
        venceHoy,
        riesgo,
        estado: data?.estado || data?.estatus || "—",
        perfilCobranza,
        raw: data,
      };
    },
    [getCliente, getFraccionamientoNombre]
  );

  useEffect(() => {
    const q = query(collection(db, "deudas"), where("activo", "==", true));

    const unsub = onSnapshot(
      q,
      async (snap) => {
        try {
          setLoading(true);

          // Pre-fetch en batch: una llamada por chunk en lugar de una por deuda.
          const deudaIds = snap.docs.map((d) => d.id);
          const clienteIds = snap.docs.flatMap((d) => {
            const data = d.data() || {};
            return [data.clienteId, data.responsableId].filter(Boolean);
          });
          const fraccIds = snap.docs
            .map((d) => agendaCobranza.getFraccionamientoIdFromDeuda(d.data() || {}))
            .filter(Boolean);

          await Promise.all([
            prefetchGestiones(deudaIds),
            prefetchClientes(clienteIds),
            prefetchFraccionamientos(fraccIds),
          ]);

          const docs = await Promise.all(snap.docs.map(enriquecer));
          const docsValidos = docs.filter(Boolean);

          const relevantes = docsValidos.filter((item) => {
            if (Number(item.saldoRestante || 0) <= 0) return false;

            const esRecuperacionReal =
              item.perfilCobranza?.esCobrable &&
              Number(item.mesesAtraso || 0) >= 1 &&
              Number(item.saldoAtrasado || 0) > 0;

            return item.venceHoy || esRecuperacionReal;
          });

          setRegistros(relevantes);
        } catch (error) {
          console.error("Error cargando agenda diaria:", error);
          setRegistros([]);
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        console.error("Error en agenda diaria:", error);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [enriquecer, prefetchGestiones, prefetchClientes, prefetchFraccionamientos]);

  // Map deudaId → promesa
  const promesasPorDeuda = useMemo(() => {
    const map = new Map();
    for (const p of promesasPendientes) {
      if (p.deudaId) map.set(p.deudaId, p);
    }
    return map;
  }, [promesasPendientes]);

  // Listas únicas para filtros
  const fraccionamientos = useMemo(() => {
    return [...new Set(registros.map((r) => r.fraccionamiento).filter(Boolean))].sort();
  }, [registros]);
  const cobradores = useMemo(() => {
    return [...new Set(registros.map((r) => r.ultimoCobrador).filter(Boolean))].sort();
  }, [registros]);

  const registrosFiltrados = useMemo(() => {
    const term = busqueda.trim().toLowerCase();

    return registros.filter((r) => {
      if (soloSinGestionReciente && Number(r.diasSinGestion || 9999) < 7) {
        return false;
      }
      if (filtroFraccionamiento !== "todos" && r.fraccionamiento !== filtroFraccionamiento) return false;
      if (filtroCobrador !== "todos" && r.ultimoCobrador !== filtroCobrador) return false;
      if (filtroRiesgo !== "todos" && r.riesgo !== filtroRiesgo) return false;
      if (!term) return true;
      return agendaCobranza.getTextoBusquedaAgenda(r).includes(term);
    });
  }, [registros, busqueda, soloSinGestionReciente, filtroFraccionamiento, filtroCobrador, filtroRiesgo]);

  // === Chips de filtros activos ===
  const filtrosActivos = useMemo(() => {
    const chips = [];
    if (busqueda.trim())
      chips.push({ key: "busq", label: `"${busqueda.trim()}"`, clear: () => setBusqueda("") });
    if (soloSinGestionReciente)
      chips.push({ key: "sing", label: "Sin gestión reciente", clear: () => setSoloSinGestionReciente(false) });
    if (filtroFraccionamiento !== "todos")
      chips.push({ key: "fracc", label: filtroFraccionamiento, clear: () => setFiltroFraccionamiento("todos") });
    if (filtroCobrador !== "todos")
      chips.push({ key: "cob", label: `Cobrador: ${filtroCobrador}`, clear: () => setFiltroCobrador("todos") });
    if (filtroRiesgo !== "todos")
      chips.push({ key: "rie", label: `Riesgo ${filtroRiesgo}`, clear: () => setFiltroRiesgo("todos") });
    return chips;
  }, [busqueda, soloSinGestionReciente, filtroFraccionamiento, filtroCobrador, filtroRiesgo]);
  const limpiarFiltros = () => {
    setBusqueda("");
    setSoloSinGestionReciente(false);
    setFiltroFraccionamiento("todos");
    setFiltroCobrador("todos");
    setFiltroRiesgo("todos");
  };

  // === Helpers de contacto ===
  const limpiarTelefono = (tel) => String(tel || "").replace(/\D/g, "");
  const copiarTelefono = async (tel) => {
    try {
      await navigator.clipboard.writeText(tel);
      notify.success("Teléfono copiado");
    } catch {
      notify.error("No se pudo copiar");
    }
  };
  const abrirWhatsApp = (item) => {
    const num = limpiarTelefono(item.telefono);
    if (!num) {
      notify.error("Sin teléfono válido");
      return;
    }
    const numConLada = num.startsWith("52") ? num : `52${num}`;
    const monto = agendaCobranza.formatMoney(item.saldoAtrasado || item.mensualidad || 0);
    const ctxt = item.venceHoy
      ? "le contacto del área de cobranza para recordarle que su mensualidad vence hoy"
      : `le contacto del área de cobranza. Su pago de ${monto} se encuentra vencido`;
    const mensaje = encodeURIComponent(
      `Buen día ${item.cliente || ""}, ${ctxt}. ¿Podríamos coordinar el pago?`,
    );
    window.open(`https://wa.me/${numConLada}?text=${mensaje}`, "_blank");
  };

  // === Selección múltiple ===
  const toggleSeleccionado = (id) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // eslint-disable-next-line no-unused-vars
  const togglearTodos = (visibles) => {
    setSeleccionados((prev) => {
      const all = visibles.every((it) => prev.has(it.id));
      if (all) {
        const next = new Set(prev);
        for (const it of visibles) next.delete(it.id);
        return next;
      }
      const next = new Set(prev);
      for (const it of visibles) next.add(it.id);
      return next;
    });
  };
  const limpiarSeleccion = () => setSeleccionados(new Set());
  const enviarWhatsAppLote = (items) => {
    if (!items.length) {
      notify.error("Sin selección");
      return;
    }
    notify.info(`Abriendo ${items.length} ventanas WhatsApp...`);
    items.forEach((it, i) => setTimeout(() => abrirWhatsApp(it), i * 300));
  };

  // === Marcar atendido / Registrar gestión rápida ===
  const marcarAtendido = async (item) => {
    try {
      await crearGestionCobranza({
        deudaId: item.id,
        clienteId: item.clienteId,
        tipo: "llamada",
        resultado: "atendido",
        notas: "Marcado como atendido desde Agenda del día",
      });
      setAtendidosHoy((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      notify.success(`${item.cliente} marcado como atendido`);
    } catch (err) {
      console.error("Error marcar atendido", err);
      notify.error("No se pudo registrar la gestión");
    }
  };

  const abrirModalGestion = (item) => {
    setModalGestion({
      item,
      tipo: "llamada",
      resultado: "contactado",
      nota: "",
    });
  };
  const confirmarModalGestion = async () => {
    if (!modalGestion) return;
    const { item, tipo, resultado, nota } = modalGestion;
    try {
      await crearGestionCobranza({
        deudaId: item.id,
        clienteId: item.clienteId,
        tipo,
        resultado,
        notas: nota || "",
      });
      setAtendidosHoy((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        return next;
      });
      setModalGestion(null);
      notify.success("Gestión registrada");
    } catch (err) {
      console.error("Error registrar gestión", err);
      notify.error("No se pudo guardar la gestión");
    }
  };

  const hoyItems = useMemo(() => {
    return registrosFiltrados
      .filter((r) => r.venceHoy)
      .sort((a, b) => {
        const atrasoDiff =
          Number(b.saldoAtrasado || 0) - Number(a.saldoAtrasado || 0);

        if (atrasoDiff !== 0) return atrasoDiff;

        return String(a.cliente || "").localeCompare(String(b.cliente || ""), "es");
      });
  }, [registrosFiltrados]);

  const moraItems = useMemo(() => {
    return registrosFiltrados
      .filter(
        (r) =>
          !r.venceHoy &&
          r.perfilCobranza?.esCobrable &&
          Number(r.mesesAtraso || 0) >= 1 &&
          Number(r.saldoAtrasado || 0) > 0
      )
      .sort((a, b) => {
        const saldoDiff =
          Number(b.saldoAtrasado || 0) - Number(a.saldoAtrasado || 0);

        if (saldoDiff !== 0) return saldoDiff;

        return Number(b.mesesAtraso || 0) - Number(a.mesesAtraso || 0);
      });
  }, [registrosFiltrados]);

 const resumen = useMemo(() => {
  const todos = [...hoyItems, ...moraItems];
  const cuentasConMoraReal = registrosFiltrados.filter(
    (item) =>
      item.perfilCobranza?.esCobrable &&
      Number(item.mesesAtraso || 0) >= 1 &&
      Number(item.saldoAtrasado || 0) > 0
  );

  return {
    // KPI 1: ATRASO REAL (igual al panel)
    moraCount: cuentasConMoraReal.length,
    moraMonto: cuentasConMoraReal.reduce(
      (acc, item) => acc + Number(item.perfilCobranza?.saldoAtrasado || 0),
      0
    ),

    // KPI 2: COBRO HOY (más realista — usa pagoMinimo, no nombre inexistente)
    hoyCount: hoyItems.length,
    hoyMonto: hoyItems.reduce((acc, item) => {
      const minimo = Number(item.perfilCobranza?.pagoMinimo || 0);
      return acc + (minimo > 0 ? minimo : Number(item.mensualidad || 0));
    }, 0),

    // KPI 3: PARA PONER AL CORRIENTE (campo correcto: pagoParaCorriente)
    totalCount: todos.length,
    paraCorriente: todos.reduce(
      (acc, item) => acc + Number(item.perfilCobranza?.pagoParaCorriente || 0),
      0
    ),

    // KPI 4: POTENCIAL DE COBRO (campo correcto: pagoIdeal)
    potencialCobro: todos.reduce(
      (acc, item) => acc + Number(item.perfilCobranza?.pagoIdeal || 0),
      0
    ),
  };
}, [moraItems, hoyItems, registrosFiltrados]);

  const handleVerDetalle = useCallback(
    (item) => {
      navigate(`/app/cliente/${item.clienteId}/deudas/${item.id}`);
    },
    [navigate]
  );

  // Fecha legible: "vence hoy" / "venció hace 3 días" / "venció hace 2 semanas"
  const fechaLegible = (nextDueDate, venceHoy) => {
    if (venceHoy) return "Vence hoy";
    const d = toJsDate(nextDueDate);
    if (!d) return "Sin fecha";
    const dias = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (dias < 0) return `Vence en ${Math.abs(dias)}d`;
    if (dias === 0) return "Vence hoy";
    if (dias === 1) return "Venció ayer";
    if (dias < 7) return `Venció hace ${dias} días`;
    if (dias < 30) return `Venció hace ${Math.floor(dias / 7)} sem`;
    if (dias < 365) return `Venció hace ${Math.floor(dias / 30)} meses`;
    return `Venció hace ${Math.floor(dias / 365)} años`;
  };

  // Export Excel del día
  const exportarExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Agenda del día");
      const hoy = new Date();
      const fechaTexto = hoy.toLocaleDateString("es-MX");
      ws.mergeCells("A1:H1");
      ws.getCell("A1").value = `AGENDA DEL DÍA — ${fechaTexto}`;
      ws.getCell("A1").font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
      ws.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1F4E78" } };
      ws.getCell("A1").alignment = { horizontal: "center" };
      const header = ws.getRow(3);
      header.values = ["Tipo", "Cliente", "Teléfono", "Fraccionamiento", "Saldo atrasado", "Mensualidad", "Estado", "Última gestión"];
      header.font = { bold: true };
      const todos = [...hoyItems.map((it) => ({ ...it, _tipo: "Hoy" })), ...moraItems.map((it) => ({ ...it, _tipo: "Vencido" }))];
      todos.forEach((it) => {
        const promesa = promesasPorDeuda.get(it.id);
        const estado = promesa ? "Con promesa" : (it.diasSinGestion < 7 ? "Contactado" : "Sin contactar");
        ws.addRow([
          it._tipo,
          it.cliente || "",
          it.telefono || "",
          it.fraccionamiento || "",
          Number(it.saldoAtrasado || 0),
          Number(it.mensualidad || 0),
          estado,
          it.ultimaGestion ? `${it.ultimaGestion.tipo || "gestion"} hace ${it.diasSinGestion}d` : "—",
        ]);
      });
      ws.getColumn(5).numFmt = "$#,##0.00";
      ws.getColumn(6).numFmt = "$#,##0.00";
      ws.columns = [{ width: 10 }, { width: 28 }, { width: 16 }, { width: 22 }, { width: 16 }, { width: 14 }, { width: 16 }, { width: 22 }];
      const buf = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buf]), `Agenda_dia_${fechaTexto.replaceAll("/", "-")}.xlsx`);
    } catch (err) {
      console.error("Excel error", err);
      notify.error("No se pudo exportar");
    }
  };

  const renderItem = (item, tipo) => {
    const promesa = promesasPorDeuda.get(item.id);
    const atendido = atendidosHoy.has(item.id);
    const fechaTxt = fechaLegible(item.nextDueDate, item.venceHoy);
    return (
      <li
        key={item.id}
        className={`agenda-item ${tipo === "hoy" ? "agenda-item--hoy" : "agenda-item--mora"} ${
          atendido ? "agenda-item--atendido" : ""
        }`}
      >
        <input
          type="checkbox"
          className="agenda-check"
          checked={seleccionados.has(item.id)}
          onChange={() => toggleSeleccionado(item.id)}
        />

        <div className="agenda-item__main">
          <div className="agenda-item-head">
            <strong className="cliente-info">{item.cliente}</strong>
            {/* Badges */}
            {promesa && (
              <span className="agenda-badge agenda-badge-promesa" title={`Promesa: ${promesa.fechaPromesa}`}>
                📅 Promesa
              </span>
            )}
            {atendido && (
              <span className="agenda-badge agenda-badge-atendido">✅ Atendido</span>
            )}
            {!atendido && Number(item.diasSinGestion || 9999) >= 7 && (
              <span className="agenda-badge agenda-badge-sin">
                🕐 {item.diasSinGestion < 999 ? `${item.diasSinGestion}d` : "—"} sin gestión
              </span>
            )}
            {item.riesgo === "alto" && (
              <span className="agenda-badge agenda-badge-riesgo-alto">🔴 Alto riesgo</span>
            )}
            {item.riesgo === "medio" && (
              <span className="agenda-badge agenda-badge-riesgo-medio">🟡 Medio</span>
            )}
          </div>

          <div className="agenda-meta-row">
            <span className="agenda-tel">
              {item.telefono || "—"}
              {item.telefono && item.telefono !== "—" && (
                <>
                  <button
                    type="button"
                    className="agenda-icon-btn"
                    title="Copiar teléfono"
                    onClick={() => copiarTelefono(item.telefono)}
                  >
                    📋
                  </button>
                  <button
                    type="button"
                    className="agenda-icon-btn agenda-icon-btn-wa"
                    title="Enviar WhatsApp"
                    onClick={() => abrirWhatsApp(item)}
                  >
                    💬
                  </button>
                </>
              )}
            </span>
            <span className={`agenda-fecha-tag ${tipo === "hoy" ? "is-hoy" : "is-vencido"}`}>
              {fechaTxt}
            </span>
            <span className="agenda-monto-tag">
              {agendaCobranza.formatMoney(item.saldoAtrasado || item.mensualidad)}
            </span>
            <span className="agenda-saldo-tag">
              Saldo: {agendaCobranza.formatMoney(item.saldoRestante)}
            </span>
          </div>

          {!vistaCompacta && (
            <div className="agenda-meta">
              <span><strong>Fracc:</strong> {item.fraccionamiento || "—"}</span>
              <span><strong>Responsable:</strong> {item.responsableNombre || "—"}</span>
              <span><strong>Referencia:</strong> {item.referencia || "—"}</span>
              <span><strong>Dirección:</strong> {item.direccion || "—"}</span>
              <span><strong>Meses transcurridos:</strong> {item.mesesTranscurridos}</span>
              <span>
                <strong>Última gestión:</strong>{" "}
                {item.ultimaGestion
                  ? `${item.ultimaGestion.resultado || item.ultimaGestion.tipo || "gestion"} (hace ${item.diasSinGestion}d)`
                  : "sin gestión"}
              </span>
            </div>
          )}
        </div>

        <div className="acciones">
          <button
            className="btn-acc btn-acc-primary"
            onClick={() => abrirModalGestion(item)}
            title="Registrar gestión sin entrar al detalle"
          >
            📝 Gestión
          </button>
          <button
            className="btn-acc btn-acc-success"
            onClick={() => marcarAtendido(item)}
            disabled={atendido}
            title="Marca como atendido (registra gestión 'contactado')"
          >
            {atendido ? "✅ Atendido" : "Marcar atendido"}
          </button>
          <button className="btn-link" onClick={() => handleVerDetalle(item)}>
            Ver detalle
          </button>
        </div>
      </li>
    );
  };

  // Agrupación por fraccionamiento
  const agruparItems = (items) => {
    if (!agruparPorFracc) return [{ key: "__all__", titulo: null, items }];
    const map = new Map();
    for (const it of items) {
      const key = it.fraccionamiento || "Sin fraccionamiento";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, list]) => ({
        key,
        titulo: `${key} (${list.length})`,
        items: list,
      }));
  };

  return (
    <div className="agenda-wrapper">
      <div className="agenda-header">
        <div>
          <h2>Agenda del día</h2>
          <p className="agenda-subtitle">
            Cobranza operativa de vencidos y cuentas que vencen hoy.
          </p>
        </div>

        <div className="agenda-header-actions">
          <button
            className="btn-pago btn-previsoria"
            onClick={() => setVistaCompacta((v) => !v)}
          >
            {vistaCompacta ? "Vista extendida" : "Vista compacta"}
          </button>
          <button
            className="btn-pago btn-previsoria"
            onClick={() => setAgruparPorFracc((v) => !v)}
          >
            {agruparPorFracc ? "Lista plana" : "Agrupar por fracc"}
          </button>
          <button
            className="btn-pago btn-previsoria"
            onClick={() => navigate("/app/agenda/previsoria")}
          >
            Previsoria
          </button>
          <button
            className="btn-pago btn-previsoria"
            onClick={() => navigate("/app/agenda/vencida")}
          >
            Vencida
          </button>
          <button className="btn-pago" onClick={exportarExcel}>
            Excel
          </button>
        </div>
      </div>

      {/* KPIs completos (4 ahora, antes solo 2) */}
      <div className="agenda-resumen">
        <div className="resumen-card resumen-card--hoy">
          <span className="resumen-card__label">Vencen hoy</span>
          <strong className="resumen-card__value">{resumen.hoyCount}</strong>
          <span className="agenda-tag agenda-tag--today">
            {agendaCobranza.formatMoney(resumen.hoyMonto)}
          </span>
        </div>

        <div className="resumen-card resumen-card--mora">
          <span className="resumen-card__label">Vencidos</span>
          <strong className="resumen-card__value">{resumen.moraCount}</strong>
          <span className="agenda-tag agenda-tag--late">
            {agendaCobranza.formatMoney(resumen.moraMonto)} atrasado
          </span>
        </div>

        <div className="resumen-card resumen-card--blue">
          <span className="resumen-card__label">Para poner al corriente</span>
          <strong className="resumen-card__value">
            {agendaCobranza.formatMoney(resumen.paraCorriente)}
          </strong>
          <span className="agenda-tag agenda-tag--saldo">
            {resumen.totalCount} cuentas activas
          </span>
        </div>

        <div className="resumen-card resumen-card--green">
          <span className="resumen-card__label">Potencial cobro</span>
          <strong className="resumen-card__value">
            {agendaCobranza.formatMoney(resumen.potencialCobro)}
          </strong>
          <span className="agenda-tag agenda-tag--money">
            Pago ideal sugerido
          </span>
        </div>
      </div>

      <div className="agenda-toolbar">
        <input
          className="agenda-search"
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar cliente, responsable, referencia, fraccionamiento, teléfono..."
        />
        <select value={filtroFraccionamiento} onChange={(e) => setFiltroFraccionamiento(e.target.value)}>
          <option value="todos">Todos los fraccionamientos</option>
          {fraccionamientos.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <select value={filtroCobrador} onChange={(e) => setFiltroCobrador(e.target.value)}>
          <option value="todos">Todos los cobradores</option>
          {cobradores.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={filtroRiesgo} onChange={(e) => setFiltroRiesgo(e.target.value)}>
          <option value="todos">Cualquier riesgo</option>
          <option value="alto">🔴 Alto</option>
          <option value="medio">🟡 Medio</option>
          <option value="bajo">🟢 Bajo</option>
        </select>
        <label className="agenda-filter-check">
          <input
            type="checkbox"
            checked={soloSinGestionReciente}
            onChange={(e) => setSoloSinGestionReciente(e.target.checked)}
          />
          Sin gestión reciente
        </label>
      </div>

      {/* Chips de filtros activos */}
      {filtrosActivos.length > 0 && (
        <div className="agenda-chips-row">
          <span className="agenda-chips-label">Filtros:</span>
          {filtrosActivos.map((chip) => (
            <button key={chip.key} className="agenda-chip" onClick={chip.clear}>
              {chip.label} <span>×</span>
            </button>
          ))}
          <button className="agenda-chip-clear" onClick={limpiarFiltros}>
            Limpiar todo
          </button>
          <span className="agenda-chips-count">
            {registrosFiltrados.length} de {registros.length}
          </span>
        </div>
      )}

      {/* Bulk bar */}
      {seleccionados.size > 0 && (
        <div className="agenda-bulk-bar">
          <span><strong>{seleccionados.size}</strong> cuentas seleccionadas</span>
          <button
            className="btn-pago"
            onClick={() =>
              enviarWhatsAppLote(
                registrosFiltrados.filter((it) => seleccionados.has(it.id)),
              )
            }
          >
            💬 Enviar WhatsApp ({seleccionados.size})
          </button>
          <button className="btn-pago btn-previsoria" onClick={limpiarSeleccion}>
            Limpiar
          </button>
        </div>
      )}

      <section className="hoy-section">
        <h3>Vencen hoy ({hoyItems.length})</h3>
        {loading ? (
          <p className="no-data">Cargando agenda...</p>
        ) : hoyItems.length === 0 ? (
          <p className="no-data">No hay cuentas que venzan hoy.</p>
        ) : (
          agruparItems(hoyItems).map((grupo) => (
            <div key={grupo.key}>
              {grupo.titulo && <h4 className="agenda-group-title">{grupo.titulo}</h4>}
              <ul className="agenda-list">
                {grupo.items.map((item) => renderItem(item, "hoy"))}
              </ul>
            </div>
          ))
        )}
      </section>

      <section className="mora-section">
        <h3>Vencidos ({moraItems.length})</h3>
        {loading ? (
          <p className="no-data">Cargando agenda...</p>
        ) : moraItems.length === 0 ? (
          <p className="no-data">No hay cuentas vencidas para mostrar.</p>
        ) : (
          agruparItems(moraItems).map((grupo) => (
            <div key={grupo.key}>
              {grupo.titulo && <h4 className="agenda-group-title">{grupo.titulo}</h4>}
              <ul className="agenda-list">
                {grupo.items.map((item) => renderItem(item, "mora"))}
              </ul>
            </div>
          ))
        )}
      </section>

      {/* Modal de gestión rápida */}
      {modalGestion && (
        <div className="agenda-modal-overlay" onClick={() => setModalGestion(null)}>
          <div className="agenda-modal" onClick={(e) => e.stopPropagation()}>
            <div className="agenda-modal-head">
              <h3>Registrar gestión</h3>
              <button className="btn-link" onClick={() => setModalGestion(null)}>✕</button>
            </div>
            <div className="agenda-modal-body">
              <p className="agenda-modal-cliente">
                <strong>{modalGestion.item.cliente}</strong>
              </p>
              <label>
                Tipo
                <select
                  value={modalGestion.tipo}
                  onChange={(e) => setModalGestion({ ...modalGestion, tipo: e.target.value })}
                >
                  <option value="llamada">Llamada</option>
                  <option value="mensaje">Mensaje (WhatsApp/SMS)</option>
                  <option value="visita">Visita</option>
                  <option value="otra">Otra</option>
                </select>
              </label>
              <label>
                Resultado
                <select
                  value={modalGestion.resultado}
                  onChange={(e) => setModalGestion({ ...modalGestion, resultado: e.target.value })}
                >
                  <option value="contactado">Contactado</option>
                  <option value="no_contestado">No contestó</option>
                  <option value="promesa">Promesa de pago</option>
                  <option value="rechazo">Rechazo</option>
                  <option value="otra">Otro</option>
                </select>
              </label>
              <label>
                Nota (opcional)
                <textarea
                  rows={3}
                  value={modalGestion.nota}
                  onChange={(e) => setModalGestion({ ...modalGestion, nota: e.target.value })}
                  placeholder="Detalle de la conversación..."
                />
              </label>
            </div>
            <div className="agenda-modal-foot">
              <button className="btn-pago btn-previsoria" onClick={() => setModalGestion(null)}>
                Cancelar
              </button>
              <button className="btn-pago" onClick={confirmarModalGestion}>
                Guardar gestión
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaDiaria;
